import type { SubjectFieldFilter, SubjectFieldSort } from '@Application/Subjects/Queries/FetchSubjects';
import type SubjectView from '@UI/Store/Subjects/Views/SubjectView';

/** Immutable View of one page of dossiers of a type, as filtered, sorted and paginated by the server. */
class SubjectsListView {
	readonly typeId: number;
	readonly search: string;
	readonly page: number;
	readonly rows: number;
	readonly totalRecords: number;
	readonly subjects: readonly SubjectView[];
	readonly fieldFilters: readonly SubjectFieldFilter[];
	readonly fieldSort: SubjectFieldSort | null;
	readonly includeArchived: boolean;

	constructor(
		typeId: number,
		search: string,
		page: number,
		rows: number,
		totalRecords: number,
		subjects: readonly SubjectView[],
		fieldFilters: readonly SubjectFieldFilter[] = [],
		fieldSort: SubjectFieldSort | null = null,
		includeArchived = false,
	) {
		this.typeId = typeId;
		this.search = search;
		this.page = page;
		this.rows = rows;
		this.totalRecords = totalRecords;
		this.subjects = subjects;
		this.fieldFilters = fieldFilters;
		this.fieldSort = fieldSort;
		this.includeArchived = includeArchived;

		Object.freeze(this);
	}

	/** Puts a created dossier first, as the list is sorted newest first. */
	addSubject(subject: SubjectView): SubjectsListView {
		return this.withSubjects(this.totalRecords + 1, [subject, ...this.subjects]);
	}

	/** Swaps the entry of an updated dossier; the list is unchanged when it is not on this page. */
	replaceSubject(subject: SubjectView): SubjectsListView {
		return this.withSubjects(
			this.totalRecords,
			this.subjects.map((entry) => (entry.id === subject.id ? subject : entry)),
		);
	}

	matches(
		typeId: number,
		search: string,
		page: number,
		rows: number,
		fieldFilters: readonly SubjectFieldFilter[] = [],
		fieldSort: SubjectFieldSort | null = null,
		includeArchived = false,
	): boolean {
		return (
			this.typeId === typeId &&
			this.search === search &&
			this.page === page &&
			this.rows === rows &&
			this.includeArchived === includeArchived &&
			this.hasSameFieldFilters(fieldFilters) &&
			this.hasSameFieldSort(fieldSort)
		);
	}

	private withSubjects(totalRecords: number, subjects: readonly SubjectView[]): SubjectsListView {
		return new SubjectsListView(
			this.typeId,
			this.search,
			this.page,
			this.rows,
			totalRecords,
			subjects,
			this.fieldFilters,
			this.fieldSort,
			this.includeArchived,
		);
	}

	private hasSameFieldFilters(fieldFilters: readonly SubjectFieldFilter[]): boolean {
		return (
			this.fieldFilters.length === fieldFilters.length &&
			this.fieldFilters.every(
				(filter, index) =>
					filter.key === fieldFilters[index]?.key && filter.value === fieldFilters[index]?.value,
			)
		);
	}

	private hasSameFieldSort(fieldSort: SubjectFieldSort | null): boolean {
		if (this.fieldSort === null || fieldSort === null) {
			return this.fieldSort === fieldSort;
		}

		return this.fieldSort.key === fieldSort.key && this.fieldSort.direction === fieldSort.direction;
	}
}

export default SubjectsListView;
